import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/he';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import { useTranslation } from 'react-i18next';
import { rtlCache, ltrCache } from './theme';
import { resolveTheme } from './theme/registry';
import { RTL_LANGUAGES, type SupportedLanguage } from './i18n';
import { useUiPrefsStore } from './stores/uiPrefsStore';
import App from './App';
import { ToastProvider } from './components/shared/ToastProvider';
import './i18n';
import './print.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function LocalizedRoot() {
  const { i18n } = useTranslation();
  const [lang, setLang] = useState<SupportedLanguage>(i18n.language as SupportedLanguage);
  const themeName = useUiPrefsStore((s) => s.theme);

  useEffect(() => {
    const handler = (lng: string) => setLang(lng as SupportedLanguage);
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, [i18n]);

  const isRtl = RTL_LANGUAGES.has(lang);
  const cache = isRtl ? rtlCache : ltrCache;
  const { theme } = resolveTheme(themeName, isRtl);
  const dayjsLocale = lang === 'ru' ? 'ru' : lang === 'en' ? 'en' : 'he';

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ToastProvider>
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <LocalizedRoot />
  </React.StrictMode>,
);
